import { DeliveryAddressType } from '../../components/DeliveryAddress/DeliveryAddress.js';
import {
  cartContainsDirectDeliveryItems,
  getOrderItemWithEnrollmentProgram,
} from '../../components/DeviceCheckout/deviceCheckoutUtils.js';
import { getDefaultDeliveryMethod } from '../../components/DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import { getErrorsFromUpsertPersonBillingAccount } from '../../components/PersonalBillingDetails/PersonalBillingDetails.js';
import { IdentityProvider } from '../../generated/api/models.js';
import { paths } from '../constants/pathVariables.js';
import { DeviceChangeOption } from '../enums.js';
import { fieldCantBeEmptyMsg, t } from '../i18n/index.js';
import { getCommercialProducts, getConfiguredCommercialProducts } from './cartProductUtils.js';
import { formatContactsToFieldValueOptions } from './contactUtils.js';
import { addEmptyFieldValidationError } from './errorUtils.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../../components/DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type {
  Address,
  CompanyInfoResponse,
  Contact,
  DeliveryAddress,
  DeliveryMethod,
  OnlineModel,
} from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type {
  FormattedContactOption,
  Price,
  ShoppingCartItemForCheckout,
  SubmitOrderProps,
} from '../types/checkout.js';
import type { ConfiguredOffer } from '../types/commercialProduct.js';
import type { DeviceChangeRequest } from '../types/device.js';
import type { CommonError } from '../types/errors.js';

// common functions

export const getDefaultAddressType = (): DeliveryAddressType => DeliveryAddressType.COMPANY_ADDRESS;

export const PriceSubType = {
  EPP_DEVICE: 'EPP_DEVICE',
};
export const SubscriptionDetailForDeviceChangeKey = 'subscription-detail-for-device-change' as const;
export const isEppDevicePriceSubType = (price: Price): boolean =>
  price.periodic?.priceSubType === PriceSubType.EPP_DEVICE;

export const isEppDevicePresentInCart = (cartItems: Array<ShoppingCartItemForCheckout>): boolean =>
  cartItems ? cartItems.some((product: ShoppingCartItemForCheckout) => isEppDevicePriceSubType(product.price)) : false;

export const EPP_PRODUCT_TYPE = 'EPP';
export const DIRECT_DELIVERY_PRODUCT_TYPE = 'DIRECTDELIVERY';
export const VAK_PRODUCT_TYPE = 'VAK';
export const PRODUCT_DELIVERY_TYPE = 'ProductDeliveryType';

export const getProductDeliveryTypesInCart = (
  cartItems: Array<ShoppingCartItemForCheckout>,
  onlineModels?: OnlineModel[]
): string[] => {
  const productDeliveryTypes = cartItems
    .map(cartItem => {
      const onlineModel = onlineModels?.find(model => model.onlineModelCode === cartItem.onlineModelCode);
      const offer = onlineModel?.offers.find(o => o.offerCode === cartItem.offerCode);

      return offer?.properties?.find(prop => prop.name === PRODUCT_DELIVERY_TYPE)?.value;
    })
    .filter(item => item !== undefined);

  if (isEppDevicePresentInCart(cartItems)) {
    productDeliveryTypes.push(EPP_PRODUCT_TYPE);
  }

  if (cartContainsDirectDeliveryItems(cartItems, onlineModels)) {
    productDeliveryTypes.push(DIRECT_DELIVERY_PRODUCT_TYPE);
  }

  return productDeliveryTypes;
};

export const showEppDeviceChangeStep = (
  cartItems: Array<ShoppingCartItemForCheckout>,
  user?: AuthenticatedUser
): boolean => {
  return Boolean(
    isEppDevicePresentInCart(cartItems) && user?.expiringEppSubscriptions && user.expiringEppSubscriptions.length
  );
};

export const getFormattedContacts = (
  contacts: Contact[] = [],
  user?: AuthenticatedUser
):
  | {
      displayOptions: string[];
      options: FormattedContactOption[];
    }
  | undefined => {
  const filteredContacts = contacts.filter(item => item.contactId !== user?.contact?.contactId);
  if (filteredContacts) {
    return formatContactsToFieldValueOptions(filteredContacts, false);
  }
  return undefined;
};

export const getDeliveryCharges = (
  deliveryDetails: DeviceCheckoutDeliveryDetailsType | undefined,
  listOfDeliveryMethods?: DeliveryMethod[]
): number => {
  return (
    deliveryDetails?.deliveryPrice ||
    (listOfDeliveryMethods ? getDefaultDeliveryMethod(listOfDeliveryMethods)?.price : 0)
  );
};

export const getErrorsFromCheckout = (
  approverContact?: string,
  personBillingAddress?: Address,
  personBillingEmail?: string,
  validationErrors?: CommonError[]
): CommonError[] | undefined => {
  const errors: CommonError[] = [];
  if (approverContact !== undefined && approverContact.length === 0) {
    addEmptyFieldValidationError(errors, 'approverContact', t.VPVR(fieldCantBeEmptyMsg));
  }
  validationErrors = getErrorsFromUpsertPersonBillingAccount(
    personBillingAddress,
    personBillingEmail,
    validationErrors
  );
  if (errors.length === 0) {
    return validationErrors;
  }
  return errors.concat(validationErrors || []);
};

export const hasEmployeeContribution = (cartItems: Array<ShoppingCartItemForCheckout>) => {
  if (!cartItems) {
    return undefined;
  }
  return getConfiguredCommercialProducts(cartItems).some(
    item => (item.commercialProduct.monthlyRecurringCharge || 0) > 0
  );
};

export const hasEmployeePayables = (
  cartItems: Array<ShoppingCartItemForCheckout>,
  deviceChangeRequest?: DeviceChangeRequest
) => Boolean(hasEmployeeContribution(cartItems) || deviceChangeRequest?.deviceChangeRedeemPrice);

const createOrderItemOffer = (item: ShoppingCartItemForCheckout) => ({
  commercialProducts: [],
  created: 1,
  lastModified: 1,
  offerCode: item.offerCode,
  offerId: '',
  offerName: '',
});

export const getOrderItems = (
  cartItems: Array<ShoppingCartItemForCheckout> = [],
  deviceChangeRequest?: DeviceChangeRequest
): ConfiguredOffer[] => {
  const commercialProducts = getCommercialProducts(cartItems);
  const orderItems: ConfiguredOffer[] = cartItems.map((cartItem, index) => ({
    // since device Offer isn't needed to place order, hence all data in orderItem offer below is vague,
    // except offerCode (only thing needed to place online order)
    // just to match the input type to the submitOrder redux action.
    key: index,
    offer: createOrderItemOffer(cartItem),
    onlineModelCode: cartItem.onlineModelCode,
    onlineModelName: cartItem.onlineModelName,
    selectedCommercialProducts: commercialProducts[index],
  }));

  if (deviceChangeRequest?.deviceChangeOption === DeviceChangeOption.RETURN) {
    let replacementDevice: ConfiguredOffer | undefined;
    orderItems.forEach(item => {
      if (
        item.selectedCommercialProducts[0].commercialProduct.commercialProductCode ===
        deviceChangeRequest?.replacementDeviceCommercialProductCode
      ) {
        if (item.selectedCommercialProducts.length > 1) {
          // if more than one quantity then there could be different POU contact (When admin orders product) hence append replacedSubscriptionId for matching contactId
          const replacementDeviceIndex = item.selectedCommercialProducts.findIndex(
            selectedCommercialProduct =>
              selectedCommercialProduct.purposeOfUseOrContact.contactId ===
              deviceChangeRequest?.replacedSubscriptionContactId
          );
          replacementDevice = {
            ...item,
            selectedCommercialProducts: [
              {
                ...item.selectedCommercialProducts[replacementDeviceIndex],
                replacedSubscriptionId: deviceChangeRequest.replacedSubscriptionId,
              },
            ],
          };
          item.selectedCommercialProducts.splice(replacementDeviceIndex, 1);
        } else {
          item.selectedCommercialProducts[0].replacedSubscriptionId = deviceChangeRequest.replacedSubscriptionId;
        }
      }
    });
    if (replacementDevice) {
      orderItems.unshift(replacementDevice);
    }
  }
  // Enrollment Program Selected Implementation
  return getOrderItemWithEnrollmentProgram(orderItems);
};

export const getCompanyName = (
  deliveryDetails?: DeviceCheckoutDeliveryDetailsType,
  user?: AuthenticatedUser,
  companyName?: string
): string =>
  deliveryDetails?.addressType === DeliveryAddressType.HOME_ADDRESS
    ? ''
    : (companyName ?? deliveryDetails?.companyName ?? user?.companyName ?? '');

export const getDeliveryDetails = (
  submitOrderProps: SubmitOrderProps,
  deliveryDetails?: DeviceCheckoutDeliveryDetailsType,
  user?: AuthenticatedUser
): DeliveryAddress => ({
  companyName: getCompanyName(deliveryDetails, user, submitOrderProps?.updatedDeliveryDetails?.companyName),
  recipient: submitOrderProps?.updatedDeliveryDetails?.recipientName ?? deliveryDetails?.recipientName ?? '',
  phoneNumber:
    submitOrderProps?.updatedDeliveryDetails?.recipientPhoneNumber ?? deliveryDetails?.recipientPhoneNumber ?? '',
  address: submitOrderProps?.updatedDeliveryDetails?.address ?? deliveryDetails?.address ?? ({} as Address),
  pickupPoint: submitOrderProps?.updatedDeliveryDetails?.pickupPoint ?? deliveryDetails?.pickupPoint,
});

export const isNotHavePermissionToBuy = (loggedIn?: boolean, user?: AuthenticatedUser) => {
  const isElisaIdUser = user?.identityProvider && user?.identityProvider === IdentityProvider.ELISA_ID_V2;
  return loggedIn && (!user?.businessId || !isElisaIdUser);
};

export const getSubscriptionDetailForDeviceChangeFromSessionStorage = ():
  | {
      contactId?: string;
      subscriptionId?: string;
    }
  | undefined => {
  const subscriptionDetailForDeviceChange = sessionStorage.getItem(SubscriptionDetailForDeviceChangeKey) || undefined;
  return subscriptionDetailForDeviceChange ? JSON.parse(subscriptionDetailForDeviceChange) : undefined;
};

// Returns true if on EOE checkout thank you page or on unified (public/NOE) checkout thank you page.
export const isFinalPage = (pathname: string) => {
  const decodedPathname = decodeURIComponent(pathname);
  return (
    decodedPathname.startsWith(paths.EMPLOYEE_DEVICE_CHECKOUT_THANK_YOU) ||
    decodedPathname.startsWith(paths.PLACE_ORDER_THANK_YOU)
  );
};

export const getSelectedDeviceChangeTitleSuffix = (deviceChangeRequest?: DeviceChangeRequest): string => {
  if (deviceChangeRequest) {
    switch (deviceChangeRequest.deviceChangeOption) {
      case DeviceChangeOption.REDEEM:
        return t.YTNE(DeviceChangeOption.REDEEM);
      case DeviceChangeOption.RETURN:
        return t.OMNZ(DeviceChangeOption.RETURN);
      case DeviceChangeOption.NONE:
        return '';
    }
  } else {
    return '';
  }
};

export const getDeliveryDetailsObj = (
  listOfDeliveryMethods?: DeliveryMethod[],
  companyInfo?: CompanyInfoResponse,
  user?: AuthenticatedUser,
  deliveryDetails?: DeviceCheckoutDeliveryDetailsType
): DeviceCheckoutDeliveryDetailsType => ({
  address:
    deliveryDetails && deliveryDetails.address
      ? deliveryDetails.address
      : companyInfo
        ? companyInfo.address
        : undefined,
  addressType: (deliveryDetails && deliveryDetails.addressType) || getDefaultAddressType(),
  deliveryMethodDescription:
    deliveryDetails?.deliveryMethodDescription || getDefaultDeliveryMethod(listOfDeliveryMethods).createDescription(),
  deliveryMethodType: deliveryDetails?.deliveryMethodType || getDefaultDeliveryMethod(listOfDeliveryMethods).type,
  deliveryPrice: deliveryDetails?.deliveryPrice || getDefaultDeliveryMethod(listOfDeliveryMethods).price,
  recipientName:
    deliveryDetails?.recipientName || (user ? user.firstName + (user.lastName ? ' ' + user.lastName : '') : ''),
  recipientPhoneNumber: deliveryDetails?.recipientPhoneNumber || user?.mobile || '',
  pickupPoint: deliveryDetails?.pickupPoint,
  companyName: deliveryDetails?.companyName || user?.companyName,
  shipmentType: deliveryDetails?.shipmentType,
});
